import { useState, useMemo } from "react"
import {ChevronLeft, ChevronRight, Calendar} from 'lucide-react'

interface DatePickerProps {
  value: string // format YYYY-MM-DD
  onChange: (date: string) => void
  minDate?: string // format YYYY-MM-DD
  label?: string
  required?: boolean
}

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]
const MOIS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
]

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`
}

function toDateStr(y: number, m: number, d: number) {
  return `${y}-${pad(m + 1)}-${pad(d)}`
}

export default function DatePicker({ value, onChange, minDate, label, required }: DatePickerProps) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const minDateObj = minDate ? new Date(minDate + "T00:00:00") : today

  const selectedDate = value ? new Date(value + "T00:00:00") : null

  const [viewYear, setViewYear] = useState(selectedDate?.getFullYear() ?? today.getFullYear())
  const [viewMonth, setViewMonth] = useState(selectedDate?.getMonth() ?? today.getMonth())

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1)
    // Lundi = 0
    let startDow = firstDay.getDay() - 1
    if (startDow < 0) startDow = 6

    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate()

    const cells: Array<{
      day: number
      month: number
      year: number
      isCurrentMonth: boolean
      isToday: boolean
      isPast: boolean
      isWeekend: boolean
      dateStr: string
    }> = []

    // Jours du mois précédent
    for (let i = startDow - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i
      const m = viewMonth - 1 < 0 ? 11 : viewMonth - 1
      const y = viewMonth - 1 < 0 ? viewYear - 1 : viewYear
      const dateObj = new Date(y, m, d)
      const dow = dateObj.getDay()
      cells.push({
        day: d,
        month: m,
        year: y,
        isCurrentMonth: false,
        isToday: false,
        isPast: dateObj < minDateObj,
        isWeekend: dow === 0 || dow === 6,
        dateStr: toDateStr(y, m, d),
      })
    }

    // Jours du mois courant
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(viewYear, viewMonth, d)
      const dow = dateObj.getDay()
      cells.push({
        day: d,
        month: viewMonth,
        year: viewYear,
        isCurrentMonth: true,
        isToday:
          d === today.getDate() &&
          viewMonth === today.getMonth() &&
          viewYear === today.getFullYear(),
        isPast: dateObj < minDateObj,
        isWeekend: dow === 0 || dow === 6,
        dateStr: toDateStr(viewYear, viewMonth, d),
      })
    }

    // Jours du mois suivant pour compléter la grille
    const remaining = 42 - cells.length
    for (let d = 1; d <= remaining; d++) {
      const m = viewMonth + 1 > 11 ? 0 : viewMonth + 1
      const y = viewMonth + 1 > 11 ? viewYear + 1 : viewYear
      const dateObj = new Date(y, m, d)
      const dow = dateObj.getDay()
      cells.push({
        day: d,
        month: m,
        year: y,
        isCurrentMonth: false,
        isToday: false,
        isPast: dateObj < minDateObj,
        isWeekend: dow === 0 || dow === 6,
        dateStr: toDateStr(y, m, d),
      })
    }

    return cells
  }, [viewYear, viewMonth, minDateObj.getTime(), today.getTime()])

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11)
      setViewYear(viewYear - 1)
    } else {
      setViewMonth(viewMonth - 1)
    }
  }

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0)
      setViewYear(viewYear + 1)
    } else {
      setViewMonth(viewMonth + 1)
    }
  }

  const canGoPrev = (() => {
    const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1
    const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear
    const lastDayPrev = new Date(prevYear, prevMonth + 1, 0)
    return lastDayPrev >= minDateObj
  })()

  const handleDayClick = (cell: (typeof calendarDays)[0]) => {
    if (cell.isPast || cell.isWeekend) return
    onChange(cell.dateStr)
  }

  const formatSelected = selectedDate
    ? `${selectedDate.getDate()} ${MOIS[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
    : null

  return (
    <div>
      {label && (
        <label className="label-field flex items-center gap-1.5 mb-2">
          <Calendar className="w-4 h-4 text-cyan-400" />
          {label}
          {required && " *"}
        </label>
      )}

      {/* Calendrier */}
      <div className="rounded-xl border border-slate-700/60 bg-slate-800/60 backdrop-blur-sm overflow-hidden">
        {/* Header navigation */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/40">
          <button
            type="button"
            onClick={goToPrevMonth}
            disabled={!canGoPrev}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-white">
            {MOIS[viewMonth]} {viewYear}
          </span>
          <button
            type="button"
            onClick={goToNextMonth}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Jours de la semaine */}
        <div className="grid grid-cols-7 px-2 pt-2">
          {JOURS.map((j, i) => (
            <div
              key={j}
              className={`text-center text-[11px] font-medium py-1.5 ${
                i >= 5 ? "text-slate-600" : "text-slate-500"
              }`}
            >
              {j}
            </div>
          ))}
        </div>

        {/* Grille des jours */}
        <div className="grid grid-cols-7 px-2 pb-3 gap-0.5">
          {calendarDays.map((cell, idx) => {
            const isSelected = value === cell.dateStr
            const isDisabled = cell.isPast || cell.isWeekend
            const isOtherMonth = !cell.isCurrentMonth

            return (
              <button
                key={idx}
                type="button"
                disabled={isDisabled}
                onClick={() => handleDayClick(cell)}
                className={`
                  relative w-full aspect-square flex items-center justify-center rounded-lg text-sm transition-all duration-150
                  ${isSelected
                    ? "bg-cyan-500 text-white font-bold shadow-lg shadow-cyan-500/30 scale-105"
                    : cell.isToday && !isDisabled
                      ? "bg-cyan-500/10 text-cyan-400 font-semibold ring-1 ring-cyan-500/30"
                      : isDisabled
                        ? "text-slate-700 cursor-not-allowed"
                        : isOtherMonth
                          ? "text-slate-600 hover:bg-slate-700/30 hover:text-slate-400"
                          : "text-slate-300 hover:bg-cyan-500/10 hover:text-cyan-300"
                  }
                `}
              >
                {cell.day}
                {cell.isToday && !isSelected && (
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-cyan-400" />
                )}
              </button>
            )
          })}
        </div>

        {/* Sélection affichée */}
        {formatSelected && (
          <div className="px-4 py-2.5 border-t border-slate-700/40 bg-slate-800/40">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-cyan-400" />
              <span className="text-slate-400">Sélectionné :</span>
              <span className="text-cyan-300 font-medium">{formatSelected}</span>
            </div>
          </div>
        )}
      </div>

      {/* Légende */}
      <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-600">
        <div className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-cyan-500/10 border border-cyan-500/30" />
          Aujourd'hui
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-slate-800 border border-slate-700" />
          Week-end (indisponible)
        </div>
      </div>

      {/* Hidden input for form validation */}
      {required && !value && (
        <input type="text" required value="" className="sr-only" onChange={() => {}} tabIndex={-1} />
      )}
    </div>
  )
}